import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Calendar, Shield, UserMinus, Share2, MapPin, Pencil, ChevronLeft, ChevronRight, Lock, UserCheck, UserX, LogOut, RefreshCw, DollarSign, MessageCircle, Trophy, Plus, Trash2, Check, Minus, FileText, BarChart3 } from 'lucide-react';
import { collection, query, orderBy, limit, onSnapshot, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAppContext } from '../../context/AppContext';
import { calculateGameStats, getCommunityRating, parseLocalDate, displayName } from '../../utils/calculations';
import Header from '../../components/layout/Header';
import Button from '../../components/ui/Button';
import PositionBadge from '../../components/ui/PositionBadge';
import TeamFlag from '../../components/ui/TeamFlag';
import Modal from '../../components/modals/Modal';
import PlayerAvatar from '../../components/ui/PlayerAvatar';
import { useToast } from '../../context/ToastContext';

const MONTH_NAMES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const getQuarterStartMonth = (month) => Math.floor(month / 3) * 3;

const getPeriodTargetDate = (period, offset) => {
  const now = new Date();
  if (period === 'year') {
    return new Date(now.getFullYear() + offset, 0, 1);
  }
  if (period === 'quarter') {
    const currentQuarterStart = getQuarterStartMonth(now.getMonth());
    return new Date(now.getFullYear(), currentQuarterStart + (offset * 3), 1);
  }
  return new Date(now.getFullYear(), now.getMonth() + offset, 1);
};

const formatPeriodLabel = (period, targetDate) => {
  if (period === 'year') return String(targetDate.getFullYear());
  if (period === 'quarter') {
    const quarter = Math.floor(targetDate.getMonth() / 3) + 1;
    return `${quarter}\u00BA trimestre de ${targetDate.getFullYear()}`;
  }
  return `${MONTH_NAMES[targetDate.getMonth()]} ${targetDate.getFullYear()}`;
};

const isDateInPeriod = (date, period, targetDate) => {
  const targetYear = targetDate.getFullYear();
  if (period === 'year') return date.getFullYear() === targetYear;
  if (period === 'quarter') {
    return date.getFullYear() === targetYear
      && Math.floor(date.getMonth() / 3) === Math.floor(targetDate.getMonth() / 3);
  }
  return date.getFullYear() === targetYear && date.getMonth() === targetDate.getMonth();
};

const getPeriodGamesLabel = (period) => {
  if (period === 'year') return 'Partidas no ano';
  if (period === 'quarter') return 'Partidas no trimestre';
  return 'Partidas no mês';
};

const GroupDetailPage = () => {
  const { groups, allGames, finances, userId, users, fetchUserProfile, removeMember, leaveGroup, readdMember, approveRequest, rejectRequest, addFinance, deleteFinance, updateGroup, deleteFinishedGame, addGame, updateGame, logAuditEvent } = useAppContext();
  const { showSuccess } = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  // Restaura aba ativa ao voltar de um jogo (histórico, jogos, etc.)
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);
  const [activeTab, setActiveTab] = useState('jogos');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ playerId: '', amount: '', playerName: '' });
  const [expenseForm, setExpenseForm] = useState({ category: 'court', amount: '', description: '' });
  const [showFormerMembers, setShowFormerMembers] = useState(false);
  const [financeOffset, setFinanceOffset] = useState(0);  // 0 = mês/semana atual
  const [classOffset, setClassOffset] = useState(0);      // 0 = mês atual
  const [deleteGameTarget, setDeleteGameTarget] = useState(null); // game obj | null
  const [deletingGame, setDeletingGame] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [classPeriod, setClassPeriod] = useState('month'); // 'month' | 'quarter' | 'year'
  const [statsOffset, setStatsOffset] = useState(0);       // 0 = período atual
  const [statsPeriod, setStatsPeriod] = useState('month'); // 'month' | 'quarter' | 'year'
  const [statsMetric, setStatsMetric] = useState('nota');  // 'nota' | 'gols' | 'assistencias'
  const [showNewGameModal, setShowNewGameModal] = useState(false);
  const [newGameForm, setNewGameForm] = useState({ date: '', time: '', maxPlayers: 14, pricePerPerson: 0 });
  const [creatingGame, setCreatingGame] = useState(false);
  const [editDateTarget, setEditDateTarget] = useState(null); // game obj | null
  const [editDateValue, setEditDateValue] = useState('');
  const [savingEditDate, setSavingEditDate] = useState(false);
  const [duplicateGameError, setDuplicateGameError] = useState('');
  // Estado dos gradientes laterais nas tabs (indica que há mais conteúdo para rolar)
  const tabsScrollRef = React.useRef(null);
  const [tabScroll, setTabScroll] = useState({ canLeft: false, canRight: false });

  const group = groups.find(g => g.id === id);

  // Carregar perfis dos membros e jogadores
  useEffect(() => {
    if (!group) return;
    const ids = new Set(group.memberIds || []);
    const groupGames = allGames.filter(g => g.groupId === id);
    groupGames.forEach(g => g.confirmedIds?.forEach(pid => ids.add(pid)));
    // Carregar perfis de solicitantes pendentes e membros anteriores
    (group.pendingRequests || []).forEach(r => ids.add(r.userId));
    (group.formerMembers || []).forEach(fm => ids.add(fm.userId));
    ids.forEach(uid => {
      if (!users[uid] && !uid.startsWith('guest_')) fetchUserProfile(uid);
    });
  }, [group, allGames, id, users, fetchUserProfile]);

  // Inicializa estado dos gradientes das tabs (após render e quando muda nº de abas)
  const _isAdminForTabs = group ? (group.ownerId === userId || (group.adminIds || []).includes(userId)) : false;
  useEffect(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const update = () => setTabScroll({
      canLeft: el.scrollLeft > 4,
      canRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [group?.scoringConfig?.enabled, _isAdminForTabs]);

  // Auto-scroll da aba ativa para a área visível — SÓ quando o activeTab muda
  // (não em re-renders causados por onScroll/etc, senão briga com scroll do user)
  useEffect(() => {
    const parent = tabsScrollRef.current;
    if (!parent) return;
    const el = parent.querySelector(`[data-tab-id="${activeTab}"]`);
    if (!el) return;
    if (parent.scrollWidth <= parent.clientWidth) return;
    const elLeft = el.offsetLeft;
    const elRight = elLeft + el.offsetWidth;
    const viewLeft = parent.scrollLeft;
    const viewRight = viewLeft + parent.clientWidth;
    if (elLeft < viewLeft || elRight > viewRight) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeTab]);

  // Escutar audit log quando aba "auditoria" estiver activa
  useEffect(() => {
    if (activeTab !== 'auditoria' || !id) return;
    setAuditLoading(true);
    const q = query(
      collection(db, 'groups', id, 'auditLog'),
      orderBy('timestamp', 'desc'),
      limit(100)
    );
    const unsub = onSnapshot(
      q,
      snap => {
        const events = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAuditLog(events);
        setAuditLoading(false);
        // Pré-carrega perfis dos atores
        events.forEach(e => {
          if (e.actorId && !users[e.actorId] && !e.actorId.startsWith('guest_')) {
            fetchUserProfile(e.actorId);
          }
        });
      },
      () => setAuditLoading(false)
    );
    return unsub;
  }, [activeTab, id, fetchUserProfile, users]);

  const classTargetDate = useMemo(() => {
    return getPeriodTargetDate(classPeriod, classOffset);
  }, [classOffset, classPeriod]);

  const classMonthLabel = useMemo(() => {
    return formatPeriodLabel(classPeriod, classTargetDate);
  }, [classTargetDate, classPeriod]);

  const classificationData = useMemo(() => {
    if (!group?.scoringConfig?.enabled) return [];
    const cfg = group.scoringConfig;
    const monthGames = allGames
      .filter(g => {
        if (g.groupId !== id || g.status !== 'finished') return false;
        return isDateInPeriod(parseLocalDate(g.date), classPeriod, classTargetDate);
      })
      .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date)); // ordem cronológica

    const stats = {}; // { uid: { pts, pj, vit, e, der, gm, gc, goals, assists, results: [] } }
    const initStats = (uid) => {
      if (!stats[uid]) stats[uid] = { pts: 0, pj: 0, vit: 0, e: 0, der: 0, gm: 0, gc: 0, goals: 0, assists: 0, results: [] };
      return stats[uid];
    };

    monthGames.forEach(game => {
      const scoreA = game.score?.teamA ?? 0;
      const scoreB = game.score?.teamB ?? 0;
      const teamAIds = (game.teams?.teamA || []).map(p => p.id).filter(pid => !pid.startsWith('guest_'));
      const teamBIds = (game.teams?.teamB || []).map(p => p.id).filter(pid => !pid.startsWith('guest_'));

      teamAIds.forEach(pid => {
        const s = initStats(pid);
        s.pj += 1; s.gm += scoreA; s.gc += scoreB;
        if (scoreA > scoreB) { s.vit += 1; s.pts += cfg.win; s.results.push('V'); }
        else if (scoreA === scoreB) { s.e += 1; s.pts += cfg.draw; s.results.push('E'); }
        else { s.der += 1; s.results.push('D'); }
      });
      teamBIds.forEach(pid => {
        const s = initStats(pid);
        s.pj += 1; s.gm += scoreB; s.gc += scoreA;
        if (scoreB > scoreA) { s.vit += 1; s.pts += cfg.win; s.results.push('V'); }
        else if (scoreA === scoreB) { s.e += 1; s.pts += cfg.draw; s.results.push('E'); }
        else { s.der += 1; s.results.push('D'); }
      });

      if (game.playerStats) {
        Object.entries(game.playerStats).forEach(([pid, st]) => {
          const s = initStats(pid);
          s.goals += st.goals || 0;
          s.assists += st.assists || 0;
        });
      }

      if (cfg.absencePenalty > 0) {
        const played = [...teamAIds, ...teamBIds];
        const invitedSubs = game.invitedSubs || {};
        (group?.memberIds || []).forEach(memberId => {
          if (!played.includes(memberId)) {
            if (invitedSubs[memberId]) return;
            const s = initStats(memberId);
            s.pts -= cfg.absencePenalty;
          }
        });
      }
    });

    // Calcula nota da galera no período alvo (média das ratings recebidas)
    const computeMonthRating = (uid) => {
      const u = users[uid];
      if (!u?.ratingHistory?.length) return null;
      const entries = u.ratingHistory.filter(h => {
        if (!h.date || h.rating == null) return false;
        return isDateInPeriod(parseLocalDate(h.date), classPeriod, classTargetDate);
      });
      if (entries.length === 0) return null;
      return Math.round((entries.reduce((s, h) => s + h.rating, 0) / entries.length) * 10) / 10;
    };

    // Período corrente: mostra todos os membros atuais (mesmo sem jogos ainda).
    // Períodos passados: só quem efetivamente participou — não polui a tabela
    // histórica com membros que entraram depois.
    const isCurrentPeriod = classOffset === 0;
    const playerSet = isCurrentPeriod
      ? new Set(group?.memberIds || [])
      : new Set(Object.keys(stats));

    return [...playerSet]
      .map(uid => {
        const s = stats[uid] || { pts: 0, pj: 0, vit: 0, e: 0, der: 0, gm: 0, gc: 0, goals: 0, assists: 0, results: [] };
        return {
          uid,
          pts: s.pts,
          pj: s.pj,
          vit: s.vit,
          e: s.e,
          der: s.der,
          gm: s.gm,
          gc: s.gc,
          sg: s.gm - s.gc,
          goals: s.goals,
          assists: s.assists,
          gaTotal: s.goals + s.assists,
          monthResults: [...s.results].reverse(), // todos os jogos do mês, mais recente primeiro
          monthRating: computeMonthRating(uid),
        };
      })
      .sort((a, b) =>
        // Ordem dos critérios: pts > pj > nota > g > a > vit > e > der > sg > gm > gc
        b.pts - a.pts ||
        b.pj - a.pj ||
        ((b.monthRating ?? -1) - (a.monthRating ?? -1)) ||
        b.goals - a.goals ||
        b.assists - a.assists ||
        b.vit - a.vit ||
        b.e - a.e ||
        a.der - b.der ||      // menos derrotas é melhor
        b.sg - a.sg ||
        b.gm - a.gm ||
        a.gc - b.gc           // menos gols sofridos é melhor
      );
  }, [group, allGames, id, users, classTargetDate, classOffset, classPeriod]);

  // ─── Estatísticas (Nota / Gols / Assistências) por período ───
  const statsTargetDate = useMemo(() => {
    return getPeriodTargetDate(statsPeriod, statsOffset);
  }, [statsOffset, statsPeriod]);

  const statsLabel = useMemo(() => {
    return formatPeriodLabel(statsPeriod, statsTargetDate);
  }, [statsTargetDate, statsPeriod]);

  const statsData = useMemo(() => {
    const periodGames = allGames.filter(g => {
      if (g.groupId !== id || g.status !== 'finished') return false;
      return isDateInPeriod(parseLocalDate(g.date), statsPeriod, statsTargetDate);
    });

    // Acumula gols/assistências/jogos por jogador
    const acc = {}; // uid -> { goals, assists, pj }
    const touch = (uid) => { if (!acc[uid]) acc[uid] = { goals: 0, assists: 0, pj: 0 }; return acc[uid]; };
    periodGames.forEach(game => {
      const played = new Set([
        ...((game.teams?.teamA || []).map(p => p.id)),
        ...((game.teams?.teamB || []).map(p => p.id)),
        ...((game.confirmedIds || [])),
      ].filter(pid => pid && !pid.startsWith('guest_')));
      played.forEach(pid => { touch(pid).pj += 1; });
      if (game.playerStats) {
        Object.entries(game.playerStats).forEach(([pid, st]) => {
          if (pid.startsWith('guest_')) return;
          const s = touch(pid);
          s.goals += st.goals || 0;
          s.assists += st.assists || 0;
        });
      }
    });

    // Nota média da galera no período (a partir do ratingHistory de cada jogador)
    const periodRating = (uid) => {
      const u = users[uid];
      if (!u?.ratingHistory?.length) return null;
      const entries = u.ratingHistory.filter(h => h.date && h.rating != null && isDateInPeriod(parseLocalDate(h.date), statsPeriod, statsTargetDate));
      if (entries.length === 0) return null;
      return Math.round((entries.reduce((s, h) => s + h.rating, 0) / entries.length) * 10) / 10;
    };

    // Conjunto de jogadores: quem participou no período (+ membros atuais no período corrente)
    const isCurrentPeriod = statsOffset === 0;
    const playerSet = new Set(Object.keys(acc));
    if (isCurrentPeriod) (group?.memberIds || []).forEach(uid => playerSet.add(uid));

    const rows = [...playerSet].map(uid => {
      const s = acc[uid] || { goals: 0, assists: 0, pj: 0 };
      return { uid, goals: s.goals, assists: s.assists, pj: s.pj, rating: periodRating(uid) };
    });

    const byMetric = {
      nota: [...rows]
        .filter(r => r.rating != null)
        .sort((a, b) => b.rating - a.rating || b.pj - a.pj),
      gols: [...rows]
        .filter(r => r.goals > 0)
        .sort((a, b) => b.goals - a.goals || (b.rating ?? -1) - (a.rating ?? -1)),
      assistencias: [...rows]
        .filter(r => r.assists > 0)
        .sort((a, b) => b.assists - a.assists || (b.rating ?? -1) - (a.rating ?? -1)),
    };
    return byMetric;
  }, [group, allGames, id, users, statsTargetDate, statsOffset, statsPeriod]);

  // ─── Dados financeiros ───
  const currentPeriod = useMemo(() => {
    const now = new Date();
    if (group?.paymentFrequency === 'monthly') {
      const d = new Date(now.getFullYear(), now.getMonth() + financeOffset, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    }
    // Weekly: ISO week com offset
    const base = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    base.setUTCDate(base.getUTCDate() + financeOffset * 7);
    const d = new Date(base);
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
  }, [group?.paymentFrequency, financeOffset]);

  const periodLabel = useMemo(() => {
    if (!currentPeriod) return '';
    if (currentPeriod.includes('W')) {
      return `Semana ${currentPeriod.split('W')[1]}`;
    }
    const [y, m] = currentPeriod.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${months[parseInt(m) - 1]} ${y}`;
  }, [currentPeriod]);

  const financeData = useMemo(() => {
    const groupFinances = finances[id] || [];
    const periodTxns = groupFinances.filter(t => t.period === currentPeriod);
    const payments = periodTxns.filter(t => t.type === 'payment');
    const expenses = periodTxns.filter(t => t.type === 'expense');
    const guestPayments = payments.filter(p => !p.playerId || p.playerName);
    const totalIn = payments.reduce((s, t) => s + (t.amount || 0), 0);
    const totalOut = expenses.reduce((s, t) => s + (t.amount || 0), 0);
    return { payments, expenses, guestPayments, totalIn, totalOut, balance: totalIn - totalOut };
  }, [finances, id, currentPeriod]);

  const financePeriodMembers = useMemo(() => {
    if (!group) return [];
    const currentMembers = group.memberIds || [];
    if (financeOffset >= 0) return currentMembers;
    // Para períodos passados: incluir também jogadores que tinham pagamentos registrados
    const paidPlayerIds = financeData.payments
      .filter(p => p.playerId && !p.playerName)
      .map(p => p.playerId);
    const allIds = new Set([...currentMembers, ...paidPlayerIds]);
    return [...allIds];
  }, [group, financeData.payments, financeOffset]);

  const financeShareData = useMemo(() => {
    if (!group) {
      return { members: [], paidMembers: [], pendingMembers: [], message: '' };
    }

    const expected = parseFloat(group.defaultPrice) || 0;
    const members = financePeriodMembers
      .map(memberId => {
        const member = users[memberId] || { photo: '??', name: 'Carregando...', position: 'Meia' };
        const isExempt = (group.exemptMembers || []).includes(memberId);
        const totalPaid = financeData.payments
          .filter(p => p.playerId === memberId)
          .reduce((sum, p) => sum + (p.amount || 0), 0);
        const isPaid = expected > 0 ? totalPaid >= expected : totalPaid > 0;
        return {
          id: memberId,
          name: displayName(member.name),
          isExempt,
          totalPaid,
          isPaid,
        };
      })
      .filter(member => !member.isExempt);

    const paidMembers = members.filter(member => member.isPaid);
    const pendingMembers = members.filter(member => !member.isPaid);

    const messageLines = [
      'Fala, galera! Tudo certo?',
      `Passando para organizar o financeiro de ${periodLabel} do grupo ${group.name}.`,
      '',
    ];

    if (paidMembers.length > 0) {
      messageLines.push('Quem ja pagou:');
      paidMembers.forEach(member => {
        messageLines.push(`[x] ${member.name}${member.totalPaid > 0 ? ` - R$ ${member.totalPaid.toFixed(0)}` : ''}`);
      });
      messageLines.push('');
    }

    if (pendingMembers.length > 0) {
      messageLines.push(`Quem ainda falta pagar${expected > 0 ? ` (R$ ${expected.toFixed(0)})` : ''}:`);
      pendingMembers.forEach(member => {
        const partial = member.totalPaid > 0 ? ` - pagou R$ ${member.totalPaid.toFixed(0)}` : '';
        messageLines.push(`[ ] ${member.name}${partial}`);
      });
      messageLines.push('');
      messageLines.push('Se conseguirem, ja me mandem para a gente fechar certinho. Valeu, pessoal!');
    } else {
      messageLines.push('Boa! Todo mundo ja acertou esse periodo. Obrigado, pessoal!');
    }

    return {
      members,
      paidMembers,
      pendingMembers,
      message: messageLines.join('\n').trim(),
    };
  }, [group, financePeriodMembers, financeData.payments, periodLabel, users]);

  if (!group) {
    return (
      <>
        <Header showBack onBack={() => navigate('/')} />
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="text-4xl mb-4">🔒</div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Grupo privado</h2>
          <p className="text-slate-400 text-sm mb-6">Voce nao tem acesso a este grupo.</p>
          <Button onClick={() => navigate('/')}>Voltar</Button>
        </div>
      </>
    );
  }

  const isAdmin = group.ownerId === userId || (group.adminIds || []).includes(userId);
  const isOwner = group.ownerId === userId;
  const members = group.memberIds || [];

  // Calcula próxima data do dia configurado no grupo (a partir de hoje)
  const getNextDateForDayOfWeek = (dayOfWeek) => {
    const dayMap = { dom: 0, seg: 1, ter: 2, qua: 3, qui: 4, sex: 5, sab: 6 };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const target = dayOfWeek ? dayMap[dayOfWeek] : null;
    if (target === null || target === undefined) return today.toISOString().split('T')[0];
    const daysUntil = ((target - today.getDay() + 7) % 7) || 7;
    const next = new Date(today);
    next.setDate(today.getDate() + daysUntil);
    return next.toISOString().split('T')[0];
  };

  const openNewGameModal = () => {
    setDuplicateGameError('');
    setNewGameForm({
      date: getNextDateForDayOfWeek(group.dayOfWeek),
      time: group.defaultTime || '20:00',
      maxPlayers: Number(group.defaultMaxPlayers) || 14,
      pricePerPerson: Number(group.defaultPrice) || 0,
    });
    setShowNewGameModal(true);
  };

  const handleCreateGame = async () => {
    if (!newGameForm.date || !newGameForm.time) return;
    setDuplicateGameError('');
    // Verifica se já existe jogo agendado/em votação para mesma data+hora
    const conflict = allGames.find(g =>
      g.groupId === id &&
      ['scheduled', 'voting'].includes(g.status) &&
      g.date === newGameForm.date &&
      g.time === newGameForm.time
    );
    if (conflict) {
      setDuplicateGameError('Já existe um jogo agendado neste horário. Edite o existente ou escolha outro horário/data.');
      return;
    }
    setCreatingGame(true);
    try {
      const max = Number(newGameForm.maxPlayers) || 14;
      await addGame(group.id, {
        date: newGameForm.date,
        time: newGameForm.time,
        maxPlayers: max,
        playersPerTeam: Math.max(1, Math.floor(max / 2) - 1),
        pricePerPerson: Number(newGameForm.pricePerPerson) || 0,
      });
      setShowNewGameModal(false);
      showSuccess('Jogo agendado!');
    } finally {
      setCreatingGame(false);
    }
  };
  const getUser = (uid) => users[uid] || { photo: '👤', name: 'Carregando...', position: 'Meia' };

  // Jogos do grupo
  const activeGames = allGames
    .filter(g => g.groupId === id && (g.status === 'scheduled' || g.status === 'voting' || g.status === 'cancelled'))
    .sort((a, b) => parseLocalDate(a.date) - parseLocalDate(b.date));
  const finishedGames = allGames
    .filter(g => g.groupId === id && g.status === 'finished')
    .sort((a, b) => parseLocalDate(b.date) - parseLocalDate(a.date));

  const pendingRequests = group.pendingRequests || [];

  const handleShareLink = () => {
    const url = `${window.location.origin}/invite/${group.id}`;
    if (navigator.share) {
      navigator.share({ title: group.name, text: 'Entra na pelada! 🏃', url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).catch(() => {});
      showSuccess('Link copiado!');
    }
  };

  const handleShareFinanceWhatsApp = () => {
    if (!financeShareData.message) return;
    const url = `https://wa.me/?text=${encodeURIComponent(financeShareData.message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleRemove = async (memberId) => {
    if (memberId === group.ownerId) return;
    await removeMember(group.id, memberId);
  };

  const tabs = [
    { id: 'jogos', label: 'Jogos', count: activeGames.length },
    { id: 'membros', label: 'Membros', count: members.length },
    { id: 'financeiro', label: 'Finanças', count: 0 },
    { id: 'historico', label: 'Histórico', count: finishedGames.length },
    ...(group.scoringConfig?.enabled ? [{ id: 'classificacao', label: 'Classif.', count: 0 }] : []),
    { id: 'estatisticas', label: 'Estatísticas', count: 0 },
    ...(isAdmin ? [{ id: 'auditoria', label: 'Auditoria', count: 0 }] : []),
  ];

  return (
    <>
      <Header showBack onBack={() => navigate('/')} />
      <div className="flex-1 overflow-y-auto">
        {/* Header do grupo */}
        <div className="px-6 pt-5 pb-4 bg-white border-b border-slate-100">
          <div className="flex justify-between items-start mb-2">
            <div className="flex-1">
              {/* Nome do grupo */}
              <h2 className="text-2xl font-bold text-slate-800">{group.name}</h2>

              {/* Tags: sport + privacidade + membros */}
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-bold rounded uppercase tracking-wide">{group.sport}</span>
                {group.isPrivate && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-bold rounded uppercase tracking-wide"><Lock className="w-2.5 h-2.5" /> Privado</span>}
                <span className="text-xs text-slate-400">{members.length} membro{members.length !== 1 ? 's' : ''}</span>
              </div>

              {/* Localização */}
              {(group.city || group.courtName) && (
                <p className="text-slate-400 text-sm flex items-center gap-1 mt-2"><MapPin className="w-3.5 h-3.5" />{[group.city, group.courtName].filter(Boolean).join(' - ')}</p>
              )}

              {/* Organizador */}
              <p className="text-xs text-slate-400 flex items-center gap-1 mt-1.5">
                <Shield className="w-3 h-3 text-blue-400" />
                Organizado por: {getUser(group.ownerId).name || 'Admin'}
              </p>

              {/* Confirmados / Votação — barra de progresso */}
              {(() => {
                const nextGame = activeGames.find(g => g.status === 'scheduled') || activeGames[0];
                if (!nextGame) return null;
                if (nextGame.status === 'voting') {
                  const extIds = nextGame.confirmedExternalCheckins || [];
                  const voted = Object.keys(nextGame.votes || {}).length;
                  const total = (nextGame.confirmedIds || []).filter(pid => !pid.startsWith('guest_') && !extIds.includes(pid)).length;
                  const remaining = total - voted;
                  return (
                    <div className="mt-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-sm font-bold text-indigo-600">{voted} de {total} votaram</span>
                        {remaining > 0 && <span className="text-sm font-medium text-slate-400">faltam {remaining}</span>}
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 rounded-full transition-all duration-300" style={{ width: `${total > 0 ? Math.min(100, (voted / total) * 100) : 0}%` }} />
                      </div>
                    </div>
                  );
                }
                const confirmed = nextGame.confirmedIds?.length || 0;
                const max = nextGame.maxPlayers || 14;
                const spots = Math.max(0, max - confirmed);
                return (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm font-bold text-slate-700">{confirmed} de {max} confirmados</span>
                      {spots > 0 && <span className="text-sm font-medium text-amber-600">faltam {spots}</span>}
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full transition-all duration-300" style={{ width: `${Math.min(100, (confirmed / max) * 100)}%` }} />
                    </div>
                  </div>
                );
              })()}
            </div>
            {isOwner && (
              <button onClick={() => navigate(`/groups/edit/${group.id}`)} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                <Pencil className="w-4 h-4 text-slate-400" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-3 mt-3">
            <div className="flex -space-x-2">
              {members.slice(0, 5).map((uid, i) => (
                <div key={i} className="w-7 h-7 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[10px]">{getUser(uid).photo}</div>
              ))}
            </div>
            <span className="text-slate-400 text-xs">{members.length} membro{members.length !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* Info cards */}
        <div className="px-6 py-3 bg-white border-b border-slate-100 flex flex-wrap gap-3">
          {group.defaultPrice && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <DollarSign className="w-3.5 h-3.5 text-emerald-500" />
              <span className="font-medium">R$ {group.defaultPrice}</span>
              <span className="text-slate-300">
                {group.paymentFrequency === 'monthly' ? '/ mês' : '/ semana'}
              </span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <RefreshCw className="w-3.5 h-3.5 text-blue-500" />
            <span className="font-medium">{group.isRecurring !== false ? 'Recorrente' : 'Jogo único'}</span>
          </div>
          {group.skipVoting && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <span className="font-medium">Sem avaliação</span>
            </div>
          )}
          {group.courtAddress && (
            <a
              href={`https://maps.google.com/maps?q=${encodeURIComponent(group.courtAddress)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-blue-600 font-medium hover:underline"
            >
              <MapPin className="w-3.5 h-3.5" /> Ver no Maps
            </a>
          )}
        </div>

        {/* Tabs — horizontal scroll no mobile, distribuído no desktop */}
        <div className="bg-white border-b border-slate-100 relative">
          {/* Fade esquerdo (visível só quando rolou para direita) */}
          {tabScroll.canLeft && (
            <div className="lg:hidden absolute left-0 top-0 bottom-[1px] w-8 bg-gradient-to-r from-white to-transparent pointer-events-none z-10" />
          )}
          {/* Fade direito (visível quando há mais conteúdo à direita) */}
          {tabScroll.canRight && (
            <div className="lg:hidden absolute right-0 top-0 bottom-[1px] w-10 bg-gradient-to-l from-white via-white/95 to-transparent pointer-events-none z-10 flex items-center justify-end pr-1.5">
              <ChevronRight className="w-4 h-4 text-slate-400" />
            </div>
          )}
          <div
            ref={tabsScrollRef}
            onScroll={e => {
              const el = e.currentTarget;
              setTabScroll({
                canLeft: el.scrollLeft > 4,
                canRight: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
              });
            }}
            className="flex overflow-x-auto px-2 lg:overflow-visible lg:px-0 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {tabs.map(tab => (
              <button
                key={tab.id}
                data-tab-id={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 lg:flex-1 px-4 py-3 text-sm font-medium transition-colors relative whitespace-nowrap ${activeTab === tab.id ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
              >
                {tab.label}
                {tab.count > 0 && <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>{tab.count}</span>}
                {activeTab === tab.id && <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-blue-500 rounded-full" />}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="p-6">
          {/* ─── JOGOS ─── */}
          {activeTab === 'jogos' && (() => {
            const hasUpcomingGame = activeGames.some(g => g.status === 'scheduled' || g.status === 'voting');
            const showAgendarButton = isAdmin && !hasUpcomingGame;
            return (
            <div className="space-y-4">
              {showAgendarButton && (
                <Button onClick={openNewGameModal} className="w-full" variant="ghost">
                  <Plus className="w-4 h-4" /> Agendar novo jogo
                </Button>
              )}
              {activeGames.length === 0 ? (
                <div className="text-center py-10 text-slate-400">
                  {isAdmin ? 'Nenhum jogo agendado. Clique acima para agendar um novo.' : 'Nenhum jogo agendado.'}
                </div>
              ) : (
                activeGames.map(game => {
                  const isVoting = game.status === 'voting';
                  const extIds = game.confirmedExternalCheckins || [];
                  const voted = Object.keys(game.votes || {}).length;
                  const totalVoters = (game.confirmedIds || []).filter(pid => !pid.startsWith('guest_') && !extIds.includes(pid)).length;
                  const confirmed = game.confirmedIds?.length || 0;
                  const spots = Math.max(0, (game.maxPlayers || 14) - confirmed);
                  return (
                    <div
                      key={game.id}
                      onClick={() => navigate(`/games/${id}/${game.id}`, { state: { groupActiveTab: activeTab } })}
                      className="bg-white rounded-xl border border-slate-200 p-4 cursor-pointer hover:shadow-md transition-all"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            <span className="text-sm font-medium text-slate-700">
                              {parseLocalDate(game.date).toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' })} - {game.time}
                            </span>
                            {game.status === 'voting' && <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">Votando</span>}
                            {game.status === 'cancelled' && <span className="text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Cancelado</span>}
                          </div>
                          <div className="flex items-center gap-3">
                            {isVoting ? (
                              <span className="text-sm font-bold text-indigo-600">{voted}/{totalVoters} votaram</span>
                            ) : (
                              <>
                                <span className={`text-sm font-bold ${spots > 4 ? 'text-blue-600' : spots > 0 ? 'text-orange-500' : 'text-red-500'}`}>
                                  {confirmed}/{game.maxPlayers}
                                </span>
                                <span className="text-xs text-slate-400">
                                  {spots > 0 ? `${spots} vaga${spots !== 1 ? 's' : ''}` : 'Lotado'}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-300" />
                      </div>
                      {game.confirmedIds?.length > 0 && (
                        <div className="flex -space-x-2 mt-3">
                          {game.confirmedIds.slice(0, 6).map((uid, i) => (
                            <div key={i} className="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[9px]">{getUser(uid).photo}</div>
                          ))}
                          {game.confirmedIds.length > 6 && <div className="w-6 h-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center text-[9px] text-slate-500 font-bold">+{game.confirmedIds.length - 6}</div>}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            );
          })()}

          {/* ─── MEMBROS ─── */}
          {activeTab === 'membros' && (
            <div>
              {/* Solicitacoes pendentes (admin only) */}
              {isAdmin && pendingRequests.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                    Solicitacoes
                    <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">{pendingRequests.length}</span>
                  </h3>
                  <div className="space-y-3">
                    {pendingRequests.map(req => {
                      const reqUser = getUser(req.userId);
                      return (
                        <div key={req.userId} className="flex items-center justify-between p-4 bg-orange-50 rounded-xl border border-orange-100">
                          <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center text-lg shadow-inner">{reqUser.photo}</div>
                            <div>
                              <div className="font-bold text-slate-700 text-sm">{reqUser.name}</div>
                              <div className="text-[10px] text-slate-400">Solicitou entrada</div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => approveRequest(group.id, req.userId)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors">
                              <UserCheck className="w-5 h-5" />
                            </button>
                            <button onClick={() => rejectRequest(group.id, req.userId)} className="p-2 text-red-400 hover:bg-red-100 rounded-lg transition-colors">
                              <UserX className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-slate-800">Membros</h3>
                <Button size="sm" variant="ghost" className="text-blue-600 bg-blue-50" onClick={handleShareLink}>
                  <Share2 className="w-3.5 h-3.5" /> Convidar
                </Button>
              </div>
              <div className="space-y-3">
                {(() => {
                  const nextGame = activeGames.find(g => g.status === 'scheduled') || null;
                  const confirmedSet = new Set(nextGame?.confirmedIds || []);
                  const absentSet = new Set(nextGame?.absentIds || []);
                  return members.map(memberId => {
                  const member = getUser(memberId);
                  const isGroupOwner = memberId === group.ownerId;
                  const isMemberAdmin = (group.adminIds || []).includes(memberId);
                  const rating = getCommunityRating(member);
                  const adminRatingVal = (group.adminRatings || {})[memberId];
                  const isConfirmed = confirmedSet.has(memberId);
                  const isAbsent = absentSet.has(memberId);
                  const cardClass = isConfirmed
                    ? 'bg-emerald-50 border-l-4 border-l-emerald-400 border border-emerald-100'
                    : isAbsent
                    ? 'bg-red-50 border-l-4 border-l-red-300 border border-red-100'
                    : 'bg-white border border-slate-100';
                  return (
                    <div key={memberId} className={`flex items-center justify-between p-4 rounded-xl relative ${cardClass}`}>
                      {isConfirmed && <span className="absolute top-2 right-2 text-[9px] font-bold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded-full">✓ confirmado</span>}
                      {isAbsent && <span className="absolute top-2 right-2 text-[9px] font-bold text-red-400 bg-red-100 px-1.5 py-0.5 rounded-full">desfalque</span>}
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <button onClick={() => navigate(`/players/${memberId}`)} className="shrink-0">
                          <PlayerAvatar user={member} size="md" />
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-slate-700 flex items-center gap-2 text-sm flex-wrap">
                            <button onClick={() => navigate(`/players/${memberId}`)} className="hover:text-blue-600 transition-colors text-left">{member.name}</button>
                            {isGroupOwner && <Shield className="w-3.5 h-3.5 text-blue-500" />}
                            {!isGroupOwner && isMemberAdmin && <span className="text-[9px] font-bold text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">Admin</span>}
                            {member.heartTeam && <TeamFlag team={member.heartTeam} />}
                          </div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <PositionBadge position={member.position} />
                            <span className="text-[10px] text-slate-400 font-medium">★ {rating?.toFixed(1) || '3.0'}</span>
                            {isAdmin ? (
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] text-indigo-500 font-bold">Nota:</span>
                                <select
                                  value={adminRatingVal || ''}
                                  onChange={e => updateGroup(id, { adminRatings: { ...(group.adminRatings || {}), [memberId]: parseInt(e.target.value) } })}
                                  className="text-[10px] bg-indigo-50 border border-indigo-100 text-indigo-700 font-bold rounded px-1 py-0.5 outline-none"
                                >
                                  <option value="">-</option>
                                  {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                              </div>
                            ) : adminRatingVal ? (
                              <span className="text-[10px] text-indigo-600 font-bold bg-indigo-50 px-1.5 py-0.5 rounded">Admin: {adminRatingVal}</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {member.phone && (
                          <a
                            href={`https://wa.me/55${member.phone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Chamar no WhatsApp"
                          >
                            <MessageCircle className="w-4 h-4" />
                          </a>
                        )}
                        {isOwner && !isGroupOwner && (
                          <button
                            onClick={async () => {
                              const currentAdmins = group.adminIds || [];
                              const newAdmins = isMemberAdmin
                                ? currentAdmins.filter(aid => aid !== memberId)
                                : [...currentAdmins, memberId];
                              await updateGroup(id, { adminIds: newAdmins });
                            }}
                            className={`p-2 rounded-lg transition-colors ${isMemberAdmin ? 'text-blue-500 hover:bg-blue-50' : 'text-slate-300 hover:text-blue-500 hover:bg-blue-50'}`}
                            title={isMemberAdmin ? 'Remover admin' : 'Tornar admin'}
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                        )}
                        {isAdmin && !isGroupOwner && !isMemberAdmin && (
                          <button onClick={() => handleRemove(memberId)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <UserMinus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                  });
                })()}
              </div>
              {!isAdmin && (
                <div className="mt-6 pt-4 border-t border-slate-100">
                  <button
                    onClick={async () => { await leaveGroup(id); navigate('/'); }}
                    className="w-full flex items-center justify-center gap-2 p-3 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl transition-colors text-sm font-medium"
                  >
                    <LogOut className="w-4 h-4" /> Sair do Grupo
                  </button>
                </div>
              )}

              {/* Membros anteriores */}
              {(group.formerMembers || []).length > 0 && (
                <div className="mt-6 pt-4 border-t border-slate-100">
                  <button onClick={() => setShowFormerMembers(v => !v)} className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-700 mb-3">
                    <span>{showFormerMembers ? '▲' : '▼'}</span> Membros anteriores ({group.formerMembers.length})
                  </button>
                  {showFormerMembers && (
                    <div className="space-y-2">
                      {group.formerMembers.map(fm => {
                        const fmUser = users[fm.userId] || { photo: '👤', name: 'Jogador' };
                        return (
                          <div key={fm.userId} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200">
                            <div className="flex items-center gap-3">
                              <div className="opacity-60"><PlayerAvatar user={fmUser} size="sm" /></div>
                              <div>
                                <div className="text-sm font-medium text-slate-500">{displayName(fmUser.name)}</div>
                                <div className="text-[10px] text-slate-400">Saiu em {new Date(fm.removedAt).toLocaleDateString('pt-BR')}</div>
                              </div>
                            </div>
                            {isAdmin && (
                              <button
                                onClick={async () => { await readdMember(id, fm.userId); }}
                                className="text-xs font-bold text-blue-500 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100"
                              >
                                Readicionar
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ─── FINANCEIRO ─── */}
          {activeTab === 'financeiro' && (
            <div className="space-y-5">
              {/* Período e resumo */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 bg-emerald-600 text-white">
                  <div className="flex justify-between items-center mb-1">
                    <button onClick={() => setFinanceOffset(o => o - 1)} className="p-1 rounded-lg hover:bg-emerald-500 text-emerald-100">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="text-center">
                      <h3 className="font-bold text-lg">{periodLabel}</h3>
                      <span className="text-emerald-200 text-xs font-medium uppercase">{group.paymentFrequency === 'monthly' ? 'Mensal' : 'Semanal'}</span>
                    </div>
                    <button onClick={() => setFinanceOffset(o => o + 1)} disabled={financeOffset >= 0} className="p-1 rounded-lg hover:bg-emerald-500 text-emerald-100 disabled:opacity-30 disabled:cursor-not-allowed">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div className="bg-emerald-500/50 rounded-lg p-2 text-center">
                      <div className="text-lg font-bold">R$ {financeData.totalIn.toFixed(0)}</div>
                      <div className="text-emerald-100 text-[10px] uppercase">Entradas</div>
                    </div>
                    <div className="bg-emerald-500/50 rounded-lg p-2 text-center">
                      <div className="text-lg font-bold">R$ {financeData.totalOut.toFixed(0)}</div>
                      <div className="text-emerald-100 text-[10px] uppercase">Saídas</div>
                    </div>
                    <div className={`rounded-lg p-2 text-center ${financeData.balance >= 0 ? 'bg-emerald-500/50' : 'bg-red-500/50'}`}>
                      <div className="text-lg font-bold">R$ {financeData.balance.toFixed(0)}</div>
                      <div className="text-emerald-100 text-[10px] uppercase">Saldo</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pagamentos dos membros */}
              <div>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="space-y-2">
                    <h3 className="font-bold text-slate-800 flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-emerald-500" /> Pagamentos
                    </h3>
                    {isAdmin && financeShareData.members.length > 0 && (
                      <button
                        onClick={handleShareFinanceWhatsApp}
                        className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full hover:bg-emerald-100"
                        title="Compartilhar cobranca no WhatsApp"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        Cobrar no Whats
                      </button>
                    )}
                  </div>
                  {isAdmin && (
                    <button onClick={() => { setPaymentForm({ playerId: '', amount: group.defaultPrice || '', playerName: '' }); setShowPaymentModal(true); }} className="flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg hover:bg-emerald-100">
                      <Plus className="w-3.5 h-3.5" /> Lancar
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {financePeriodMembers.map(memberId => {
                    const member = getUser(memberId);
                    const isExempt = (group.exemptMembers || []).includes(memberId);
                    const memberPayments = financeData.payments.filter(p => p.playerId === memberId);
                    const totalPaid = memberPayments.reduce((s, p) => s + (p.amount || 0), 0);
                    const expected = parseFloat(group.defaultPrice) || 0;
                    const isPaid = totalPaid >= expected && expected > 0;
                    return (
                      <div key={memberId} className={`flex items-center justify-between p-3 rounded-xl border ${isExempt ? 'bg-blue-50 border-blue-100' : isPaid ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-slate-100'}`}>
                        <div className="flex items-center gap-3">
                          <PlayerAvatar user={member} size="sm" />
                          <div>
                            <div className="text-sm font-bold text-slate-700 truncate">{displayName(member.name)}</div>
                            {isExempt ? (
                              <div className="text-[10px] text-blue-500 font-medium">Isento</div>
                            ) : totalPaid > 0 ? (
                              <div className="text-[10px] text-emerald-600 font-medium">R$ {totalPaid.toFixed(0)} pago</div>
                            ) : null}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isExempt ? (
                            <span className="flex items-center gap-1 text-xs font-bold text-blue-500 bg-blue-100 px-2 py-1 rounded-full">Isento</span>
                          ) : isPaid ? (
                            <span className="flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-100 px-2 py-1 rounded-full"><Check className="w-3 h-3" /> Pago</span>
                          ) : (
                            <span className="text-xs text-slate-400">{expected > 0 ? `R$ ${expected.toFixed(0)}` : '—'}</span>
                          )}
                          {isAdmin && !isExempt && (
                            <button
                              onClick={() => { setPaymentForm({ playerId: memberId, amount: expected || '', playerName: '' }); setShowPaymentModal(true); }}
                              className={`p-1.5 rounded-lg ${isPaid ? 'text-blue-400 hover:bg-blue-50' : 'text-emerald-500 hover:bg-emerald-100'}`}
                              title={isPaid ? 'Editar pagamento' : 'Lançar pagamento'}
                            >
                              {isPaid ? <Pencil className="w-3.5 h-3.5" /> : <Check className="w-4 h-4" />}
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={async () => {
                                const exempt = group.exemptMembers || [];
                                const updated = isExempt ? exempt.filter(id => id !== memberId) : [...exempt, memberId];
                                await updateGroup(group.id, { exemptMembers: updated });
                              }}
                              className={`p-1.5 rounded-lg text-xs ${isExempt ? 'text-blue-500 hover:bg-blue-100' : 'text-slate-300 hover:bg-slate-100 hover:text-slate-500'}`}
                              title={isExempt ? 'Remover isenção' : 'Marcar como isento'}
                            >
                              <Shield className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Saídas / Despesas */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    <Minus className="w-4 h-4 text-red-500" /> Saídas
                  </h3>
                  {isAdmin && (
                    <button onClick={() => { setExpenseForm({ category: 'court', amount: '', description: '' }); setShowExpenseModal(true); }} className="flex items-center gap-1 text-xs font-bold text-red-500 bg-red-50 px-3 py-1.5 rounded-lg hover:bg-red-100">
                      <Plus className="w-3.5 h-3.5" /> Registrar
                    </button>
                  )}
                </div>
                {financeData.expenses.length === 0 ? (
                  <div className="text-center py-6 text-slate-400 text-sm bg-slate-50 rounded-xl border border-dashed border-slate-200">Nenhuma saída registrada.</div>
                ) : (
                  <div className="space-y-2">
                    {financeData.expenses.map(exp => (
                      <div key={exp.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-red-50 flex items-center justify-center text-base">
                            {exp.category === 'court' ? '⚽' : exp.category === 'churras' ? '🍖' : '📦'}
                          </div>
                          <div>
                            <div className="text-sm font-bold text-slate-700">
                              {exp.category === 'court' ? 'Quadra' : exp.category === 'churras' ? 'Churras' : 'Outro'}
                            </div>
                            {exp.description && <div className="text-[10px] text-slate-400">{exp.description}</div>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-red-500">- R$ {exp.amount?.toFixed(0)}</span>
                          {isAdmin && (
                            <button onClick={() => deleteFinance(id, exp.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Histórico de pagamentos do período */}
              {financeData.payments.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Lançamentos do período</h4>
                  <div className="space-y-1">
                    {financeData.payments.map(p => (
                      <div key={p.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg text-xs">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500">{p.playerName || displayName(getUser(p.playerId).name) || 'Avulso'}</span>
                          <span className="font-bold text-emerald-600">+ R$ {p.amount?.toFixed(0)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {p.createdAt && <span className="text-slate-400">{new Date(p.createdAt.seconds * 1000).toLocaleDateString('pt-BR')}</span>}
                          {isAdmin && (
                            <button onClick={() => deleteFinance(id, p.id)} className="p-1 text-slate-300 hover:text-red-500">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── HISTORICO ─── */}
          {activeTab === 'historico' && (
            <div className="space-y-4">
              {finishedGames.length === 0 ? (
                <div className="text-center text-slate-400 py-10">Nenhum jogo finalizado.</div>
              ) : (
                finishedGames.map(game => {
                  const stats = calculateGameStats(game, users);
                  return (
                    <div key={game.id} onClick={() => navigate(`/games/${id}/${game.id}`, { state: { groupActiveTab: activeTab } })} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm cursor-pointer hover:shadow-md transition-all">
                      <div className="p-4 bg-slate-50 flex justify-between items-center border-b border-slate-100">
                        <div className="text-xs font-bold text-slate-500 uppercase">{parseLocalDate(game.date).toLocaleDateString('pt-BR')}</div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 font-bold text-slate-800"><span className="text-indigo-600">A {game.score?.teamA}</span><span className="text-slate-300">-</span><span className="text-orange-600">{game.score?.teamB} B</span></div>
                          {isAdmin && (
                            <>
                              <button
                                onClick={e => { e.stopPropagation(); setEditDateTarget(game); setEditDateValue(game.date); }}
                                className="p-1.5 text-slate-300 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Corrigir data do jogo"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={e => { e.stopPropagation(); setDeleteGameTarget(game); }}
                                className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Excluir jogo"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="p-4 space-y-3">
                        {/* Resumo dos destaques — linha compacta. Detalhe completo no clique. */}
                        {(() => {
                          const items = [];
                          if (stats.mvp) items.push({ emoji: '🏆', label: 'Craque', user: stats.mvp.user, value: stats.mvp.avg.toFixed(1), color: 'text-amber-600 bg-amber-50' });
                          if (stats.topScorer) items.push({ emoji: '⚽', label: 'Artilheiro', user: stats.topScorer.user, value: stats.topScorer.goals, color: 'text-emerald-700 bg-emerald-50' });
                          if (stats.topAssister) items.push({ emoji: '🍽️', label: 'Garçom', user: stats.topAssister.user, value: stats.topAssister.assists, color: 'text-blue-700 bg-blue-50' });
                          if (stats.pereba && (!stats.mvp || stats.pereba.id !== stats.mvp.id)) items.push({ emoji: '🩹', label: 'Bola Murcha', user: stats.pereba.user, value: stats.pereba.avg.toFixed(1), color: 'text-red-600 bg-red-50' });
                          if (items.length === 0) return null;
                          return (
                            <div className="flex flex-wrap gap-1.5">
                              {items.map((it, idx) => (
                                <div key={idx} className={`flex items-center gap-1.5 ${it.color} rounded-full pl-1 pr-2 py-0.5`}>
                                  <span className="text-xs">{it.emoji}</span>
                                  <span className="text-[10px] font-medium truncate max-w-[6rem]">{displayName(it.user?.name) || '—'}</span>
                                  <span className="text-[10px] font-bold opacity-70">· {it.value}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                        {/* Times do dia */}
                        {game.teams && (
                          <div className="flex gap-3 text-xs">
                            <div className="flex-1 bg-indigo-50 rounded-lg p-2">
                              <div className="text-[9px] uppercase font-bold text-indigo-500 mb-1">Time A</div>
                              <div className="text-slate-600">{game.teams.teamA?.map(p => displayName(p.name)).join(', ')}</div>
                            </div>
                            <div className="flex-1 bg-orange-50 rounded-lg p-2">
                              <div className="text-[9px] uppercase font-bold text-orange-500 mb-1">Time B</div>
                              <div className="text-slate-600">{game.teams.teamB?.map(p => displayName(p.name)).join(', ')}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
          {/* ─── CLASSIFICAÇÃO ─── */}
          {activeTab === 'classificacao' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-slate-800 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Classificação</h3>
                <div className="flex gap-2 text-[10px] font-bold text-slate-400">
                  <span className="bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded">V={group.scoringConfig?.win}pts</span>
                  <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">E={group.scoringConfig?.draw}pts</span>
                  {group.scoringConfig?.absencePenalty > 0 && <span className="bg-red-50 text-red-500 px-1.5 py-0.5 rounded">F=-{group.scoringConfig?.absencePenalty}pts</span>}
                </div>
              </div>
              {/* Toggle Mes / Trimestre / Ano */}
              <div className="flex bg-slate-100 rounded-xl p-1 mb-3">
                <button
                  onClick={() => { setClassPeriod('month'); setClassOffset(0); }}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${classPeriod === 'month' ? 'bg-white text-amber-600 shadow-sm' : 'text-slate-500'}`}
                >
                  Mes
                </button>
                <button
                  onClick={() => { setClassPeriod('quarter'); setClassOffset(0); }}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${classPeriod === 'quarter' ? 'bg-white text-amber-600 shadow-sm' : 'text-slate-500'}`}
                >
                  Trimestre
                </button>
                <button
                  onClick={() => { setClassPeriod('year'); setClassOffset(0); }}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${classPeriod === 'year' ? 'bg-white text-amber-600 shadow-sm' : 'text-slate-500'}`}
                >
                  Ano
                </button>
              </div>
              <div className="flex items-center justify-between mb-4 bg-slate-50 rounded-xl px-3 py-2">
                <button onClick={() => setClassOffset(o => o - 1)} className="p-1 rounded-lg hover:bg-slate-200 text-slate-500">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-semibold text-slate-700">{classMonthLabel}</span>
                <button onClick={() => setClassOffset(o => o + 1)} disabled={classOffset >= 0} className="p-1 rounded-lg hover:bg-slate-200 text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              {classificationData.length === 0 ? (
                <div className="text-center text-slate-400 py-10 text-sm">Nenhum jogo finalizado em {classMonthLabel}.</div>
              ) : (
                <>
                  <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                          <tr>
                            <th className="text-left px-2 py-2.5 font-semibold">#</th>
                            <th className="text-left pl-1 pr-2 py-2.5 font-semibold">Clube</th>
                            <th className="px-2 py-2.5 font-semibold text-center">Pts</th>
                            <th className="px-2 py-2.5 font-semibold text-center">PJ</th>
                            <th className="px-2 py-2.5 font-semibold text-center">Nota</th>
                            <th className="px-2 py-2.5 font-semibold text-center">G</th>
                            <th className="px-2 py-2.5 font-semibold text-center">A</th>
                            <th className="px-2 py-2.5 font-semibold text-center">VIT</th>
                            <th className="px-2 py-2.5 font-semibold text-center">E</th>
                            <th className="px-2 py-2.5 font-semibold text-center">DER</th>
                            <th className="px-2 py-2.5 font-semibold text-center">SG</th>
                            <th className="px-2 py-2.5 font-semibold text-center">GM</th>
                            <th className="px-2 py-2.5 font-semibold text-center">GC</th>
                            <th className="px-2 py-2.5 font-semibold text-center whitespace-nowrap">{getPeriodGamesLabel(classPeriod)}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {classificationData.map((entry, i) => {
                            const member = getUser(entry.uid);
                            const total = classificationData.length;
                            const leaderZone = group.scoringConfig?.leaderZone ?? 2;
                            const relegZone = group.scoringConfig?.relegationZone ?? 2;
                            const isTop = total >= (leaderZone + relegZone + 1) && i < leaderZone;
                            const isBottom = total >= (leaderZone + relegZone + 1) && i >= total - relegZone;
                            const zoneCellBorder = isTop
                              ? 'border-l-4 border-l-green-500'
                              : isBottom
                              ? 'border-l-4 border-l-red-500'
                              : 'border-l-4 border-l-transparent';
                            return (
                              <tr key={entry.uid} className="hover:bg-slate-50 transition-colors">
                                <td className={`px-2 py-2.5 text-slate-400 font-bold text-xs ${zoneCellBorder}`}>{i + 1}</td>
                                <td className="pl-1 pr-2 py-2.5">
                                  <button onClick={() => navigate(`/players/${entry.uid}`)} className="flex items-center gap-2 text-left hover:text-blue-600 transition-colors">
                                    <PlayerAvatar user={member} size="xs" />
                                    <span className="font-medium text-slate-700 whitespace-nowrap text-xs">{displayName(member.name, true)}</span>
                                  </button>
                                </td>
                                <td className="px-2 py-2.5 text-center font-bold text-slate-800">{entry.pts}</td>
                                <td className="px-2 py-2.5 text-center text-slate-500 text-xs">{entry.pj}</td>
                                <td className="px-2 py-2.5 text-center text-amber-600 font-medium text-xs">
                                  {entry.monthRating != null ? entry.monthRating.toFixed(1) : '—'}
                                </td>
                                <td className={`px-2 py-2.5 text-center font-medium text-xs ${entry.goals > 0 ? 'text-emerald-700' : 'text-slate-400'}`}>{entry.goals}</td>
                                <td className={`px-2 py-2.5 text-center font-medium text-xs ${entry.assists > 0 ? 'text-blue-700' : 'text-slate-400'}`}>{entry.assists}</td>
                                <td className="px-2 py-2.5 text-center text-emerald-600 font-medium text-xs">{entry.vit}</td>
                                <td className="px-2 py-2.5 text-center text-blue-600 font-medium text-xs">{entry.e}</td>
                                <td className="px-2 py-2.5 text-center text-red-500 font-medium text-xs">{entry.der}</td>
                                <td className={`px-2 py-2.5 text-center font-medium text-xs ${entry.sg > 0 ? 'text-emerald-600' : entry.sg < 0 ? 'text-red-500' : 'text-slate-500'}`}>
                                  {entry.sg > 0 ? `+${entry.sg}` : entry.sg}
                                </td>
                                <td className="px-2 py-2.5 text-center text-slate-500 text-xs">{entry.gm}</td>
                                <td className="px-2 py-2.5 text-center text-slate-500 text-xs">{entry.gc}</td>
                                <td className="px-2 py-2.5">
                                  <div className="flex gap-1 justify-center flex-nowrap">
                                    {entry.monthResults.length === 0 ? (
                                      <span className="text-slate-300 text-[10px]">—</span>
                                    ) : entry.monthResults.map((r, idx) => (
                                      <span
                                        key={idx}
                                        className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                          r === 'V' ? 'bg-emerald-500 text-white' :
                                          r === 'E' ? 'bg-slate-300 text-slate-700' :
                                          'bg-red-500 text-white'
                                        }`}
                                        title={r === 'V' ? 'Vitória' : r === 'E' ? 'Empate' : 'Derrota'}
                                      >
                                        {r}
                                      </span>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {classificationData.length >= 4 && (
                    <div className="flex items-center gap-4 pt-2 justify-center flex-wrap">
                      <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-green-500" /><span className="text-[10px] text-slate-500">Liderança</span></div>
                      <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-red-500" /><span className="text-[10px] text-slate-500">Zona de corte</span></div>
                    </div>
                  )}
                  <div className="bg-slate-50 rounded-xl p-3 mt-2 space-y-2">
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Legenda das colunas</p>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-slate-500">
                      <div><span className="font-bold text-slate-700">Pts</span> · pontos</div>
                      <div><span className="font-bold text-slate-700">PJ</span> · partidas jogadas</div>
                      <div><span className="font-bold text-amber-600">Nota</span> · nota da galera no período</div>
                      <div><span className="font-bold text-emerald-700">G</span> · gols marcados pelo jogador</div>
                      <div><span className="font-bold text-blue-700">A</span> · assistências</div>
                      <div><span className="font-bold text-emerald-600">VIT</span> · vitórias</div>
                      <div><span className="font-bold text-blue-600">E</span> · empates</div>
                      <div><span className="font-bold text-red-500">DER</span> · derrotas</div>
                      <div><span className="font-bold text-slate-700">SG</span> · saldo de gols (GM − GC)</div>
                      <div><span className="font-bold text-slate-700">GM</span> · gols feitos pelo time</div>
                      <div><span className="font-bold text-slate-700">GC</span> · gols sofridos pelo time</div>
                    </div>
                    <p className="text-[10px] text-slate-400 pt-1 border-t border-slate-200">
                      <span className="font-bold">Critérios de desempate:</span> Pts → PJ → Nota → G → A → VIT → E → DER (menos) → SG → GM → GC (menos)
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ─── ESTATÍSTICAS ─── */}
          {activeTab === 'estatisticas' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-bold text-slate-800 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-500" /> Estatísticas</h3>
              </div>

              {/* Sub-abas: Nota / Gols / Assistências */}
              <div className="flex bg-slate-100 rounded-xl p-1">
                {[
                  { id: 'nota', label: '⭐ Nota' },
                  { id: 'gols', label: '⚽ Gols' },
                  { id: 'assistencias', label: '🅰️ Assist.' },
                ].map(m => (
                  <button
                    key={m.id}
                    onClick={() => setStatsMetric(m.id)}
                    className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${statsMetric === m.id ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500'}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {/* Toggle Mes / Trimestre / Ano */}
              <div className="flex bg-slate-100 rounded-xl p-1">
                <button onClick={() => { setStatsPeriod('month'); setStatsOffset(0); }} className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${statsPeriod === 'month' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500'}`}>Mes</button>
                <button onClick={() => { setStatsPeriod('quarter'); setStatsOffset(0); }} className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${statsPeriod === 'quarter' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500'}`}>Trimestre</button>
                <button onClick={() => { setStatsPeriod('year'); setStatsOffset(0); }} className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${statsPeriod === 'year' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-500'}`}>Ano</button>
              </div>

              {/* Navegação de período */}
              <div className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2">
                <button onClick={() => setStatsOffset(o => o - 1)} className="p-1 rounded-lg hover:bg-slate-200 text-slate-500"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-sm font-semibold text-slate-700">{statsLabel}</span>
                <button onClick={() => setStatsOffset(o => o + 1)} disabled={statsOffset >= 0} className="p-1 rounded-lg hover:bg-slate-200 text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight className="w-4 h-4" /></button>
              </div>

              {(() => {
                const rows = statsData[statsMetric] || [];
                const metricMeta = {
                  nota:         { unit: '★', getVal: r => r.rating?.toFixed(1) ?? '—', color: 'text-amber-600',   emptyMsg: 'Nenhuma nota registrada' },
                  gols:         { unit: 'gols', getVal: r => r.goals,   color: 'text-emerald-700', emptyMsg: 'Nenhum gol registrado' },
                  assistencias: { unit: 'assist.', getVal: r => r.assists, color: 'text-blue-700',  emptyMsg: 'Nenhuma assistência registrada' },
                }[statsMetric];
                if (rows.length === 0) {
                  return <div className="text-center text-slate-400 py-10 text-sm">{metricMeta.emptyMsg} em {statsLabel}.</div>;
                }
                return (
                  <div className="space-y-2">
                    {rows.map((entry, i) => {
                      const member = getUser(entry.uid);
                      const medal = i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-200 text-slate-600' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-slate-50 text-slate-400';
                      return (
                        <button
                          key={entry.uid}
                          onClick={() => navigate(`/players/${entry.uid}`)}
                          className="w-full flex items-center gap-3 rounded-xl border border-slate-100 p-3 bg-white hover:bg-slate-50 transition-colors text-left"
                        >
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${medal}`}>{i + 1}</div>
                          <PlayerAvatar user={member} size="sm" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-slate-700 truncate">{member.name}</div>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              <PositionBadge position={member.position} />
                              <span className="text-[10px] text-slate-400">{entry.pj} jogo{entry.pj !== 1 ? 's' : ''}</span>
                              {/* Secundárias contextuais */}
                              {statsMetric !== 'nota' && entry.rating != null && <span className="text-[10px] text-amber-500 font-medium">★ {entry.rating.toFixed(1)}</span>}
                              {statsMetric !== 'gols' && entry.goals > 0 && <span className="text-[10px] text-emerald-600 font-medium">⚽ {entry.goals}</span>}
                              {statsMetric !== 'assistencias' && entry.assists > 0 && <span className="text-[10px] text-blue-600 font-medium">🅰️ {entry.assists}</span>}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className={`text-xl font-black ${metricMeta.color}`}>{metricMeta.getVal(entry)}</div>
                            <div className="text-[9px] text-slate-400 uppercase tracking-wide">{metricMeta.unit}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ─── AUDITORIA ─── */}
          {activeTab === 'auditoria' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-500" /> Auditoria
                </h3>
                <span className="text-[10px] text-slate-400">últimas 100 ações</span>
              </div>
              <p className="text-xs text-slate-400">
                Registro de ações sensíveis no grupo: início e cancelamento de votações,
                finalizações, exclusões e cancelamentos de jogos.
              </p>
              {auditLoading ? (
                <div className="text-center py-6 text-sm text-slate-400">Carregando…</div>
              ) : auditLog.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-400">
                  Nenhuma ação registrada ainda.
                </div>
              ) : (
                <div className="space-y-2">
                  {auditLog.map(event => {
                    const actor = users[event.actorId] || { name: event.actorName || 'Usuário' };
                    const ts = event.timestamp?.toDate ? event.timestamp.toDate() : null;
                    const dateStr = ts ? ts.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
                    const timeStr = ts ? ts.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';
                    const actionMeta = {
                      'game.live_started':     { label: 'Iniciou ao vivo',         color: 'bg-red-100 text-red-700',       icon: '🔴' },
                      'game.live_stopped':     { label: 'Cancelou ao vivo',        color: 'bg-slate-100 text-slate-600',   icon: '⏹️' },
                      'game.voting_started':   { label: 'Iniciou votação',         color: 'bg-indigo-100 text-indigo-700', icon: '🗳️' },
                      'game.voting_reverted':  { label: 'Cancelou votação',        color: 'bg-red-100 text-red-700',       icon: '↩️' },
                      'game.finalized':        { label: 'Finalizou jogo',          color: 'bg-emerald-100 text-emerald-700', icon: '🏁' },
                      'game.cancelled':        { label: 'Cancelou jogo',           color: 'bg-amber-100 text-amber-700',   icon: '🚫' },
                      'game.reactivated':      { label: 'Reativou jogo',           color: 'bg-blue-100 text-blue-700',     icon: '🔄' },
                      'game.deleted':          { label: 'Excluiu jogo',            color: 'bg-red-100 text-red-700',       icon: '🗑️' },
                      'game.deleted_finished': { label: 'Excluiu jogo finalizado', color: 'bg-red-100 text-red-700',       icon: '🗑️' },
                      'game.date_edited':      { label: 'Corrigiu data do jogo',  color: 'bg-purple-100 text-purple-700', icon: '📅' },
                    }[event.action] || { label: event.action, color: 'bg-slate-100 text-slate-600', icon: '•' };

                    // Encontrar jogo relacionado para mostrar data
                    const relatedGame = allGames.find(g => g.id === event.targetId);
                    const gameLabel = relatedGame
                      ? `${parseLocalDate(relatedGame.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} — ${relatedGame.time}`
                      : (event.details?.date ? `${parseLocalDate(event.details.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}` : null);

                    return (
                      <div key={event.id} className="bg-white border border-slate-100 rounded-xl p-3 flex gap-3">
                        <div className="text-xl shrink-0">{actionMeta.icon}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${actionMeta.color}`}>{actionMeta.label}</span>
                            <span className="text-[10px] text-slate-400">{dateStr} {timeStr}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-sm text-slate-700">
                            <PlayerAvatar user={actor} size="xs" />
                            <span className="font-medium truncate">{displayName(actor.name)}</span>
                          </div>
                          {gameLabel && (
                            <div className="text-xs text-slate-500 mt-1">Jogo: {gameLabel}</div>
                          )}
                          {/* Detalhes específicos por tipo de ação */}
                          {event.action === 'game.voting_reverted' && event.details?.votesLost > 0 && (
                            <div className="text-xs text-red-600 font-medium mt-1">
                              ⚠️ {event.details.votesLost} {event.details.votesLost === 1 ? 'voto perdido' : 'votos perdidos'}
                            </div>
                          )}
                          {event.action === 'game.finalized' && event.details?.score && (
                            <div className="text-xs text-slate-500 mt-1">
                              Placar: {event.details.score.teamA ?? 0} × {event.details.score.teamB ?? 0}
                              {event.details.votesCount > 0 && (
                                <span className="text-slate-400"> · {event.details.votesCount} {event.details.votesCount === 1 ? 'voto' : 'votos'}</span>
                              )}
                            </div>
                          )}
                          {event.action === 'game.deleted_finished' && event.details?.affectedPlayers > 0 && (
                            <div className="text-xs text-slate-500 mt-1">
                              {event.details.affectedPlayers} {event.details.affectedPlayers === 1 ? 'jogador afetado' : 'jogadores afetados'}
                            </div>
                          )}
                          {event.action === 'game.date_edited' && event.details?.from && event.details?.to && (
                            <div className="text-xs text-slate-500 mt-1">
                              <span className="font-mono text-red-500 line-through">{parseLocalDate(event.details.from).toLocaleDateString('pt-BR')}</span>
                              <span className="mx-1">→</span>
                              <span className="font-mono text-emerald-600 font-bold">{parseLocalDate(event.details.to).toLocaleDateString('pt-BR')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal: Lançar Pagamento */}
      {/* Modal: Corrigir data do jogo finalizado */}
      <Modal isOpen={!!editDateTarget} onClose={() => setEditDateTarget(null)} title="Corrigir data do jogo">
        {editDateTarget && (() => {
          const original = parseLocalDate(editDateTarget.date);
          const newD = editDateValue ? parseLocalDate(editDateValue) : null;
          const monthChanged = newD && (newD.getMonth() !== original.getMonth() || newD.getFullYear() !== original.getFullYear());
          return (
            <div className="space-y-4">
              <p className="text-sm text-slate-500 text-center">
                Use isso para lançamentos retroativos — quando o jogo foi finalizado com a data errada (ex: data do próximo jogo).
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs space-y-1">
                <div className="text-slate-500">Data atual: <span className="font-mono font-bold text-slate-700">{original.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' })}</span></div>
                <div className="text-slate-500">Placar: <span className="font-bold text-slate-700">{editDateTarget.score?.teamA ?? 0} × {editDateTarget.score?.teamB ?? 0}</span></div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 mb-1 block">Nova data</label>
                <input
                  type="date"
                  value={editDateValue}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={e => setEditDateValue(e.target.value)}
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400"
                />
                <p className="text-[10px] text-slate-400 mt-1">A data não pode ser futura.</p>
              </div>
              {monthChanged && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
                  <p className="font-bold mb-1">⚠️ Mês diferente</p>
                  <p>Esta correção move o jogo de mês. Isso afeta a <strong>classificação</strong> e o <strong>financeiro</strong> dos meses envolvidos — ambos serão recalculados automaticamente.</p>
                </div>
              )}
              <div className="flex gap-3">
                <Button variant="secondary" className="flex-1" onClick={() => setEditDateTarget(null)} disabled={savingEditDate}>
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  disabled={savingEditDate || !editDateValue || editDateValue === editDateTarget.date}
                  onClick={async () => {
                    setSavingEditDate(true);
                    try {
                      const oldDate = editDateTarget.date;
                      const newDate = editDateValue;
                      // Atualiza o documento do jogo
                      await updateGame(group.id, editDateTarget.id, { date: newDate });
                      // Atualiza date nas entradas do ratingHistory de cada jogador afetado
                      // para que a classificação mensal continue consistente
                      try {
                        const affectedIds = new Set([
                          ...(editDateTarget.confirmedIds || []),
                          ...((editDateTarget.teams?.teamA || []).map(p => p.id)),
                          ...((editDateTarget.teams?.teamB || []).map(p => p.id)),
                        ]);
                        for (const uid of affectedIds) {
                          if (uid.startsWith('guest_')) continue;
                          const profRef = doc(db, 'users', uid, 'profile', 'data');
                          const snap = await getDoc(profRef);
                          if (!snap.exists()) continue;
                          const data = snap.data();
                          const history = data.ratingHistory || [];
                          let changed = false;
                          const newHistory = history.map(h => {
                            if (h.gameId === editDateTarget.id && h.date !== newDate) {
                              changed = true;
                              return { ...h, date: newDate };
                            }
                            return h;
                          });
                          if (changed) await updateDoc(profRef, { ratingHistory: newHistory });
                        }
                      } catch (e) {
                        // Não bloqueia — pode ser permissão Firestore
                      }
                      logAuditEvent && logAuditEvent(group.id, 'game.date_edited', editDateTarget.id, { from: oldDate, to: newDate });
                      setEditDateTarget(null);
                      showSuccess('Data corrigida.');
                    } finally {
                      setSavingEditDate(false);
                    }
                  }}
                >
                  {savingEditDate ? 'Salvando…' : 'Salvar nova data'}
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Modal: Novo Jogo */}
      <Modal isOpen={showNewGameModal} onClose={() => setShowNewGameModal(false)} title="Agendar novo jogo">
        <div className="space-y-4">
          <p className="text-xs text-slate-500">
            Os campos foram pré-preenchidos com as configurações padrão do grupo.
          </p>
          {group?.isRecurring !== false && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700">
              <p className="font-bold mb-1">ℹ️ Grupo recorrente</p>
              <p>Este grupo já cria o próximo jogo automaticamente após cada finalização. Use isso só para jogos extras ou em outro dia/horário — agendar para a mesma data+hora de um jogo existente será bloqueado.</p>
            </div>
          )}
          {duplicateGameError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700 font-medium">
              {duplicateGameError}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1 block">Data</label>
              <input
                type="date"
                value={newGameForm.date}
                onChange={e => setNewGameForm({ ...newGameForm, date: e.target.value })}
                className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1 block">Horário</label>
              <input
                type="time"
                value={newGameForm.time}
                onChange={e => setNewGameForm({ ...newGameForm, time: e.target.value })}
                className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1 block">Máx. jogadores</label>
              <input
                type="number"
                min="2"
                max="99"
                value={newGameForm.maxPlayers}
                onChange={e => setNewGameForm({ ...newGameForm, maxPlayers: e.target.value })}
                className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1 block">Preço (R$)</label>
              <input
                type="number"
                min="0"
                value={newGameForm.pricePerPerson}
                onChange={e => setNewGameForm({ ...newGameForm, pricePerPerson: e.target.value })}
                className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setShowNewGameModal(false)} disabled={creatingGame}>
              Cancelar
            </Button>
            <Button
              className="flex-1"
              onClick={handleCreateGame}
              disabled={creatingGame || !newGameForm.date || !newGameForm.time}
            >
              {creatingGame ? 'Agendando…' : 'Agendar'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} title="Lançar Entrada">
        <div className="space-y-4">
          {/* Tabs: Membro ou Entrada livre */}
          <div className="flex p-1 bg-slate-100 rounded-xl">
            <button
              onClick={() => setPaymentForm({ ...paymentForm, playerId: '', playerName: '' })}
              className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${!paymentForm.playerName && paymentForm.playerId !== '__free__' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400'}`}
            >
              Membro
            </button>
            <button
              onClick={() => setPaymentForm({ ...paymentForm, playerId: '__free__', playerName: '' })}
              className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${paymentForm.playerId === '__free__' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400'}`}
            >
              Entrada livre
            </button>
          </div>

          {paymentForm.playerId !== '__free__' ? (
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1 block">Jogador</label>
              <select
                value={paymentForm.playerId}
                onChange={e => setPaymentForm({ ...paymentForm, playerId: e.target.value })}
                className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400"
              >
                <option value="">Selecionar...</option>
                {members.map(uid => (
                  <option key={uid} value={uid}>{getUser(uid).name}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1 block">Descrição</label>
              <input
                type="text"
                value={paymentForm.playerName}
                onChange={e => setPaymentForm({ ...paymentForm, playerName: e.target.value })}
                className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400"
                placeholder="Ex: Pagamento avulso João"
              />
            </div>
          )}
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1 block">Valor (R$)</label>
            <input
              type="number"
              value={paymentForm.amount}
              onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })}
              className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400"
              placeholder="0"
            />
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setShowPaymentModal(false)}>Cancelar</Button>
            <Button
              className="flex-1"
              disabled={!paymentForm.amount || (paymentForm.playerId !== '__free__' && !paymentForm.playerId) || (paymentForm.playerId === '__free__' && !paymentForm.playerName.trim())}
              onClick={async () => {
                const data = {
                  type: 'payment',
                  playerId: paymentForm.playerId === '__free__' ? '' : paymentForm.playerId,
                  playerName: paymentForm.playerId === '__free__' ? paymentForm.playerName.trim() : '',
                  amount: parseFloat(paymentForm.amount),
                  period: currentPeriod,
                };
                await addFinance(id, data);
                setShowPaymentModal(false);
              }}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal: Registrar Saída */}
      <Modal isOpen={showExpenseModal} onClose={() => setShowExpenseModal(false)} title="Registrar Saída">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1 block">Categoria</label>
            <div className="flex gap-2">
              {[
                { id: 'court', label: 'Quadra', icon: '⚽' },
                { id: 'churras', label: 'Churras', icon: '🍖' },
                { id: 'other', label: 'Outro', icon: '📦' },
              ].map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setExpenseForm({ ...expenseForm, category: cat.id })}
                  className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border text-sm font-medium transition-colors ${expenseForm.category === cat.id ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-slate-200 text-slate-500 hover:border-blue-200'}`}
                >
                  <span className="text-lg">{cat.icon}</span>
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1 block">Valor (R$)</label>
            <input
              type="number"
              value={expenseForm.amount}
              onChange={e => setExpenseForm({ ...expenseForm, amount: e.target.value })}
              className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400"
              placeholder="0"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 mb-1 block">Descrição (opcional)</label>
            <input
              type="text"
              value={expenseForm.description}
              onChange={e => setExpenseForm({ ...expenseForm, description: e.target.value })}
              className="w-full p-3 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400"
              placeholder="Ex: Quadra do mês"
            />
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setShowExpenseModal(false)}>Cancelar</Button>
            <Button
              className="flex-1"
              disabled={!expenseForm.amount}
              onClick={async () => {
                await addFinance(id, { type: 'expense', category: expenseForm.category, amount: parseFloat(expenseForm.amount), description: expenseForm.description, period: currentPeriod });
                setShowExpenseModal(false);
              }}
            >
              Registrar
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Modal: confirmar exclusão de jogo finalizado ── */}
      <Modal isOpen={!!deleteGameTarget} onClose={() => setDeleteGameTarget(null)} title="Excluir jogo do histórico?">
        {deleteGameTarget && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 text-center">
              Jogo de <strong>{parseLocalDate(deleteGameTarget.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</strong>
            </p>
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-1 text-xs text-red-700">
              <p className="font-bold">⚠️ Esta ação é irreversível:</p>
              <p>• O jogo será removido permanentemente do histórico</p>
              <p>• As notas recebidas pelos jogadores neste jogo serão removidas do perfil de cada um</p>
              <p>• Classificação e financeiro serão atualizados automaticamente</p>
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => setDeleteGameTarget(null)} disabled={deletingGame}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                disabled={deletingGame}
                onClick={async () => {
                  setDeletingGame(true);
                  try {
                    await deleteFinishedGame(id, deleteGameTarget.id);
                    setDeleteGameTarget(null);
                    showSuccess('Jogo excluído do histórico.');
                  } finally {
                    setDeletingGame(false);
                  }
                }}
              >
                {deletingGame ? 'Excluindo…' : 'Excluir'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

    </>
  );
};

export default GroupDetailPage;

